/*
 * d1-deferred-media-fallback.js — Sicherheitsnetz für <deferred-media>
 *
 * Warum (Jasper 2026-08-12): Videos liegen im Theme in
 * <deferred-media><template>…</template></deferred-media>. Der Template-Inhalt
 * kommt NUR ins DOM, wenn der IntersectionObserver in main.js anspringt und
 * loadContent() ruft. Sprang der nicht an, blieb das <video> reiner
 * Template-Inhalt: nie geladen, nie sichtbar — im Theme-Editor lief das Video,
 * auf der Seite nicht.
 *
 * Dieses Skript prüft dieselbe Bedingung noch einmal selbst, aber über
 * getBoundingClientRect statt über einen Observer, und löst nach — mit derselben
 * 1000px-Schwelle wie main.js. Damit bleibt das Lazy-Loading erhalten: ohne
 * Sichtbarkeit wird nichts geladen (ein Video sind hier gut 6 MB).
 *
 * Ausdrücklich NICHT angefasst:
 *  - deferred-media mit .js-load-media (Produktgalerie): die sollen erst auf
 *    Klick laden, nicht vorab.
 *  - Elemente ohne Ausdehnung, z.B. `hidden md:block` auf Mobil: dort soll
 *    bewusst kein Video-Traffic entstehen.
 */
(() => {
  const MARGIN = 1000;

  const eligible = (dm) => {
    if (typeof dm.loadContent !== 'function') return false;
    if (dm.querySelector('.js-load-media')) return false;

    /*
     * Slideshow: nur der aktive Slide. Die Geometrie allein reicht hier nicht —
     * beim `load`-Event steht das Slider-Layout noch nicht final, und ein
     * seitlich liegender Slide rutschte durch die Rect-Prüfung (gemessen: der
     * Video-Slide 2 der Startseite zog 6,75 MB, obwohl Slide 1 aktiv war).
     * `slideshow.js` pflegt `is-active`; beim Weiterblättern scrollt es den
     * Container, unser Capture-Listener sieht das und zieht dann nach.
     */
    const slide = dm.closest('.slideshow__slide');
    if (slide && !slide.classList.contains('is-active')) return false;

    const r = dm.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    /*
     * Beide Achsen prüfen. Vertikal mit derselben 1000px-Vorlaufzone wie main.js,
     * horizontal OHNE Vorlauf — sonst lädt der Startseiten-Slider alle Slides auf
     * einmal: das ist ein horizontales Karussell, alle Slides liegen auf gleicher
     * Höhe nebeneinander (Slide 2 z.B. bei left: 1440). Eine rein vertikale
     * Prüfung hielt jeden davon für sichtbar — bei 5 Video-Slides wären das
     * 5 x ~6,75 MB. main.js macht das über echte 2D-Intersection richtig.
     */
    const vertikal = r.top < window.innerHeight + MARGIN && r.bottom > -MARGIN;
    const horizontal = r.left < window.innerWidth && r.right > 0;
    return vertikal && horizontal;
  };

  /*
   * Listener bleiben bewusst aktiv, auch wenn gerade nichts offen ist: die
   * Produktliste wird per Pagination/Filter nachgeladen, dann kommen neue
   * deferred-media ins DOM. Ein früherer Entwurf hat sich nach dem ersten
   * vollständigen Durchlauf abgemeldet und war für nachgeladene Blöcke tot.
   * getElementsByTagName ist live — per Pagination nachgeladene <deferred-media>
   * tauchen von selbst in der Collection auf, ohne erneute Dokument-Abfrage.
   * Kosten pro Scroll-Tick damit nur noch eine Iteration über die (kleine)
   * Collection; Layout-Reads (getBoundingClientRect) fallen erst an, wenn
   * wirklich ein ungeladenes Element ansteht.
   */
  const allDeferred = document.getElementsByTagName('deferred-media');

  const check = () => {
    for (let i = 0; i < allDeferred.length; i += 1) {
      const dm = allDeferred[i];
      if (!dm.hasAttribute('loaded') && eligible(dm)) dm.loadContent(false, false, 'observer');
    }
  };

  /*
   * Drosselung über setTimeout, nicht requestAnimationFrame: rAF ruht in
   * versteckten Tabs komplett, damit wäre das Nachziehen dort tot — und in der
   * Testumgebung (Browser-Pane läuft als hidden) nicht verifizierbar.
   */
  let queued = false;
  function onScroll() {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      check();
    }, 200);
  }

  /*
   * Scroll im Capture-Modus am document, nicht nur am window: der Slider scrollt
   * einen eigenen Container (.slideshow, overflow-x: auto). Solche Element-Scrolls
   * bubbeln nicht, sind in der Capture-Phase aber zu sehen — sonst zieht das
   * Nachladen beim Weiterblättern nicht.
   */
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll, { passive: true });

  if (document.readyState === 'complete') {
    setTimeout(check, 0);
  } else {
    window.addEventListener('load', check);
  }
})();
